import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { Tilbake, Sidetopp } from "../ny-kundesamhandling/Skjema";
import SesongSmsValg from "../components/dekkhotell/SesongSmsValg";
import { skrivUtEtikett } from "../utils/sykkelhotellEtikett";
import { kundeNavn, fmtDato, lagerstedForButikk } from "../constants/dekkhotell";
import {
  STATUS_KORT,
  STATUS_CLS,
  STED_LABEL,
  SESONG_SMS_SPORSMAL,
  sykkelTekst,
} from "../constants/sykkelhotell";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

async function hentJson(url, opts) {
  const r = await fetch(url, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || "Noe gikk galt");
  return data;
}

// Om høsten minnes tidligere kunder på at vi tar imot sykler, om våren minnes
// kundene med sykkel inne på å si fra før henting.
const paminnelseNa = (d = new Date()) => (d.getMonth() >= 6 ? "host" : "henting");

// ---------------------------------------------------------------------------
// Ny innlevering
// ---------------------------------------------------------------------------
function NyInnlevering({ forvalgtKundeId, onOpprettet, onAvbryt }) {
  const [sok, setSok] = useState("");
  const [treff, setTreff] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [sykler, setSykler] = useState([]);
  const [bikeId, setBikeId] = useState("");
  const [sykkel, setSykkel] = useState("");
  const [elsykkel, setElsykkel] = useState(false);
  const [batteriMed, setBatteriMed] = useState(true);
  const [medfolger, setMedfolger] = useState("");
  const [tilstand, setTilstand] = useState("");
  const [innlevertSted, setInnlevertSted] = useState(lagerstedForButikk(Cookies.get("butikkid")));
  const [serviceRef, setServiceRef] = useState("");
  const [betaltTil, setBetaltTil] = useState("");
  const [sesongSms, setSesongSms] = useState(null);
  const [feil, setFeil] = useState("");
  const [lagrer, setLagrer] = useState(false);

  useEffect(() => {
    if (!forvalgtKundeId) return;
    hentJson(`${API_BASE_URL}/customers/${forvalgtKundeId}`)
      .then(setKunde)
      .catch(() => setFeil("Fant ikke kunden"));
  }, [forvalgtKundeId]);

  useEffect(() => {
    if (kunde || sok.trim().length < 2) return setTreff([]);
    const t = setTimeout(() => {
      hentJson(`${API_BASE_URL}/customers/search?q=${encodeURIComponent(sok.trim())}`)
        .then(setTreff)
        .catch(() => setTreff([]));
    }, 250);
    return () => clearTimeout(t);
  }, [sok, kunde]);

  useEffect(() => {
    if (!kunde?._id) return setSykler([]);
    hentJson(`${API_BASE_URL}/bikes?customerId=${encodeURIComponent(kunde._id)}`)
      .then(setSykler)
      .catch(() => setSykler([]));
  }, [kunde]);

  // Har kunden svart før (en tidligere vinter), er svaret valgt på forhånd.
  useEffect(() => {
    setSesongSms(null);
    if (!kunde?._id) return;
    hentJson(`${API_BASE_URL}/sykkelhotell/sesong-sms/${kunde._id}`)
      .then((d) => setSesongSms(d.verdi))
      .catch(() => setSesongSms(null));
  }, [kunde]);

  // Sykkelkortet vet om det er en elsykkel.
  useEffect(() => {
    const b = sykler.find((x) => x._id === bikeId);
    if (b?.elsykkel) setElsykkel(true);
  }, [bikeId, sykler]);

  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    if (!kunde) return setFeil("Velg kunde først.");
    if (!bikeId && !sykkel.trim()) return setFeil("Velg sykkelkort eller beskriv sykkelen.");
    if (!innlevertSted) return setFeil("Velg hvor sykkelen ble levert.");
    if (sesongSms === null) return setFeil("Spør kunden om SMS når sykkelhotellet åpner igjen.");
    setLagrer(true);
    try {
      const post = await hentJson(`${API_BASE_URL}/sykkelhotell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: kunde._id,
          bikeId: bikeId || undefined,
          sykkel,
          elsykkel,
          batteriMed: elsykkel && batteriMed,
          medfolger,
          tilstand,
          butikkid: parseInt(Cookies.get("butikkid"), 10) || undefined,
          innlevertSted,
          serviceRef,
          betaltTil: betaltTil || undefined,
          sesongSms,
        }),
      });
      onOpprettet(post);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  const felt = "w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30";
  const etikett = "mb-1 block text-sm font-semibold text-slate-800";

  return (
    <form onSubmit={lagre} className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 space-y-5">
      <h2 className="text-lg font-bold text-slate-900">Ta imot sykkel til sykkelhotellet</h2>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">1. Kunde og sykkel</h3>
        {kunde ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">{kundeNavn(kunde)}</span>
            <span className="text-slate-600 text-sm">{kunde.phoneNumber}</span>
            {!forvalgtKundeId && (
              <button type="button" onClick={() => setKunde(null)} className="text-sm text-indigo-700 underline">
                Bytt kunde
              </button>
            )}
          </div>
        ) : (
          <div className="relative max-w-md">
            <label htmlFor="sh-kundesok" className={etikett}>Søk på navn eller telefon</label>
            <input
              id="sh-kundesok"
              type="search"
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              className={felt}
              autoComplete="off"
            />
            {treff.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow">
                {treff.map((k) => (
                  <li key={k._id}>
                    <button
                      type="button"
                      onClick={() => setKunde(k)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50"
                    >
                      {kundeNavn(k)} <span className="text-slate-600 text-sm">{k.phoneNumber}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {kunde && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="sh-sykkelkort" className={etikett}>Sykkelkort</label>
              <select id="sh-sykkelkort" value={bikeId} onChange={(e) => setBikeId(e.target.value)} className={felt}>
                <option value="">Ingen / ikke registrert</option>
                {sykler.map((b) => (
                  <option key={b._id} value={b._id}>
                    {[b.merke, b.modell, b.elsykkel ? "(elsykkel)" : ""].filter(Boolean).join(" ")} · {b.shortId}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="sh-sykkel" className={etikett}>Sykkel (fritekst)</label>
              <input
                id="sh-sykkel"
                type="text"
                placeholder="F.eks. svart Trek Allant+ 7"
                value={sykkel}
                onChange={(e) => setSykkel(e.target.value)}
                className={felt}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-center md:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={elsykkel} onChange={(e) => setElsykkel(e.target.checked)} />
                Elsykkel
              </label>
              {elsykkel && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={batteriMed} onChange={(e) => setBatteriMed(e.target.checked)} />
                  Batteriet lagres med sykkelen
                </label>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label htmlFor="sh-medfolger" className={etikett}>Følger med (lås, veske, lader …)</label>
          <input id="sh-medfolger" value={medfolger} onChange={(e) => setMedfolger(e.target.value)} className={felt} />
        </div>
        <div>
          <label htmlFor="sh-tilstand" className={etikett}>Synlige skader eller merknader ved innlevering</label>
          <input
            id="sh-tilstand"
            placeholder="F.eks. ripe på overrøret"
            value={tilstand}
            onChange={(e) => setTilstand(e.target.value)}
            className={felt}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="sh-sted" className={etikett}>Levert hos</label>
          <select id="sh-sted" value={innlevertSted} onChange={(e) => setInnlevertSted(e.target.value)} className={felt}>
            <option value="">Velg sted</option>
            {Object.entries(STED_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sh-serviceref" className={etikett}>Verkstedordre (betaling)</label>
          <input
            id="sh-serviceref"
            type="text"
            placeholder="F.eks. RSS-1234"
            value={serviceRef}
            onChange={(e) => setServiceRef(e.target.value)}
            className={felt}
          />
        </div>
        <div>
          <label htmlFor="sh-betalttil" className={etikett}>Lagring betalt til</label>
          <input id="sh-betalttil" type="date" value={betaltTil} onChange={(e) => setBetaltTil(e.target.value)} className={felt} />
        </div>
      </section>

      <SesongSmsValg
        idPrefix="sh-ny"
        tittel="SMS når sykkelhotellet åpner igjen"
        sporsmal={SESONG_SMS_SPORSMAL}
        verdi={sesongSms}
        onChange={(v) => {
          setSesongSms(v);
          setFeil("");
        }}
        disabled={!kunde}
      />

      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={lagrer}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {lagrer ? "Lagrer…" : "Ta imot og skriv ut etikett"}
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">
          Avbryt
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sesongpåminnelse på SMS
// ---------------------------------------------------------------------------
function Paminnelse({ onSendt }) {
  const [type, setType] = useState(paminnelseNa());
  const [info, setInfo] = useState(null);
  const [tekst, setTekst] = useState("");
  const [status, setStatus] = useState("");
  const [sender, setSender] = useState(false);

  useEffect(() => {
    setInfo(null);
    setStatus("");
    hentJson(`${API_BASE_URL}/sykkelhotell/paminnelse?type=${type}`)
      .then((d) => {
        setInfo(d);
        setTekst(d.standardtekst);
      })
      .catch((e) => setStatus(e.message));
  }, [type]);

  const mottakere = info?.mottakere || [];
  const antall = mottakere.filter((m) => m.harTelefon && m.sesongSms === true).length;
  const sagtNei = mottakere.filter((m) => m.sesongSms === false).length;
  const ikkeSpurt = mottakere.filter((m) => m.sesongSms == null).length;

  async function send() {
    if (!window.confirm(`Sende påminnelse på SMS til ${antall} kunder?`)) return;
    setSender(true);
    setStatus("");
    try {
      const r = await hentJson(`${API_BASE_URL}/sykkelhotell/paminnelse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, tekst }),
      });
      setStatus(
        `Sendt: ${r.sendt}. Feilet: ${r.feilet}. Uten telefon: ${r.utenTelefon}.` +
          (type === "host" ? ` Sagt nei: ${r.sagtNei}. Ikke spurt: ${r.ikkeSpurt}.` : "")
      );
      onSendt?.();
      setInfo(await hentJson(`${API_BASE_URL}/sykkelhotell/paminnelse?type=${type}`));
    } catch (e) {
      setStatus(e.message);
    } finally {
      setSender(false);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
      <h2 className="text-lg font-bold text-slate-900 mb-1">Sesongpåminnelse på SMS</h2>
      <p className="text-sm text-slate-600 mb-3">
        {type === "henting"
          ? "Går til alle med sykkel på lageret som ikke har bestilt henting. Det er en del av avtalen, så den krever ikke ja til SMS."
          : "Går til tidligere sykkelhotell-kunder uten sykkel inne nå, som har sagt ja til SMS når sykkelhotellet åpner igjen."}{" "}
        Ikke til samme kunde to ganger på 90 dager. Bruk {"{nr}"} for nummeret. Ikke skriv navn i teksten.
      </p>
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div>
          <label htmlFor="sh-pam-type" className="mb-1 block text-sm font-semibold text-slate-800">Hvem</label>
          <select
            id="sh-pam-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="p-2 border border-slate-300 rounded-lg"
          >
            <option value="henting">Har sykkel på lageret (våren: si fra før henting)</option>
            <option value="host">Tidligere kunder (høsten: vi tar imot sykler)</option>
          </select>
        </div>
        <p className="text-sm text-slate-700 py-2">
          {info ? `${antall} mottakere` : "Henter …"}
          {info && sagtNei > 0 && ` · ${sagtNei} har sagt nei`}
          {info && ikkeSpurt > 0 && ` · ${ikkeSpurt} er ikke spurt`}
        </p>
      </div>
      <label htmlFor="sh-pam-tekst" className="mb-1 block text-sm font-semibold text-slate-800">Tekst</label>
      <textarea
        id="sh-pam-tekst"
        rows={3}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        className="w-full p-2 border border-slate-300 rounded-lg"
      />
      <p className="text-xs text-slate-600 mt-1">
        Ca. {tekst.replaceAll("{fornavn}", "").replaceAll("{nr}", "SH-0000").length} tegn med
        nummer (over 160 blir to SMS-er per kunde)
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button
          type="button"
          onClick={send}
          disabled={sender || antall === 0}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {sender ? "Sender…" : "Send påminnelse"}
        </button>
        {status && <p role="status" className="text-sm text-slate-800">{status}</p>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Oversikten
// ---------------------------------------------------------------------------
export default function Sykkelhotell() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const forvalgtKundeId = params.get("kunde") || "";
  const [visNy, setVisNy] = useState(!!forvalgtKundeId);
  const [visPaminnelse, setVisPaminnelse] = useState(false);
  const [status, setStatus] = useState("aktive");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ poster: [], kapasitet: null, perStatus: {} });
  const [laster, setLaster] = useState(true);
  const [feil, setFeil] = useState("");

  const hent = useCallback(async () => {
    setLaster(true);
    setFeil("");
    try {
      const qs = new URLSearchParams({ status });
      if (q.trim()) qs.set("q", q.trim());
      setData(await hentJson(`${API_BASE_URL}/sykkelhotell?${qs}`));
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLaster(false);
    }
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(hent, 250);
    return () => clearTimeout(t);
  }, [hent]);

  function opprettet(post) {
    setVisNy(false);
    if (forvalgtKundeId) setParams({});
    skrivUtEtikett(post);
    navigate(`/sykkelhotell/${post._id}`);
  }

  const k = data.kapasitet;
  const ps = data.perStatus || {};

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp
        ikon="hotell"
        farge="lager"
        tittel="Sykkelhotell"
        undertittel={<>
          {k && (
            <>
              <p className="text-sm text-slate-800">
                <strong>{k.ledige} ledige</strong> av {k.plasser} plasser · {k.opptatt} opptatt
                {k.reservert > 0 && ` · ${k.reservert} reservert fra nettbookinger`}
                {k.ledige === 0 && <span className="ml-2 font-semibold text-red-700">Fullt på nettsiden</span>}
              </p>
              <div
                className="mt-1 h-2 w-64 max-w-full rounded-lg bg-slate-200 overflow-hidden"
                role="img"
                aria-label={`${k.opptatt + k.reservert} av ${k.plasser} plasser er tatt`}
              >
                <div
                  className="h-full bg-indigo-600"
                  style={{ width: `${Math.min(100, ((k.opptatt + k.reservert) / k.plasser) * 100)}%` }}
                />
              </div>
            </>
          )}
          <p className="text-sm text-slate-600 mt-1">
            Venter på transport: {ps.innlevert || 0} · Henting bestilt: {ps["henting-bestilt"] || 0} · Klar til
            henting: {ps.klar || 0}
          </p>
        </>}
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setVisPaminnelse((v) => !v)}
            className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            aria-expanded={visPaminnelse}
          >
            Sesongpåminnelse
          </button>
          <button
            type="button"
            onClick={() => setVisNy((v) => !v)}
            className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            aria-expanded={visNy}
          >
            + Ta imot sykkel
          </button>
        </div>
      </Sidetopp>

      {visPaminnelse && <Paminnelse onSendt={hent} />}
      {visNy && (
        <NyInnlevering
          forvalgtKundeId={forvalgtKundeId}
          onOpprettet={opprettet}
          onAvbryt={() => {
            setVisNy(false);
            if (forvalgtKundeId) setParams({});
          }}
        />
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <label htmlFor="sh-sok" className="sr-only">Søk</label>
          <input
            id="sh-sok"
            type="search"
            placeholder="Søk på SH-nr, kunde, telefon, sykkel eller plass"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
          />
        </div>
        <label htmlFor="sh-status" className="sr-only">Status</label>
        <select id="sh-status" value={status} onChange={(e) => setStatus(e.target.value)} className="p-2 border border-slate-300 rounded-lg">
          <option value="aktive">Alle som ikke er utlevert</option>
          {Object.entries(STATUS_KORT).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
          <option value="alle">Alle</option>
        </select>
      </div>

      {feil && <p role="alert" className="text-red-700 mb-3">{feil}</p>}
      {laster ? (
        <p className="text-slate-600">Laster …</p>
      ) : data.poster.length === 0 ? (
        <p className="text-slate-600">Ingen treff.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-slate-200 rounded-xl text-sm">
            <thead>
              <tr className="bg-slate-100 text-left text-slate-700">
                <th className="px-3 py-2">Nr</th>
                <th className="px-3 py-2">Kunde</th>
                <th className="px-3 py-2">Sykkel</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Plass / henting</th>
                <th className="px-3 py-2">Betalt til</th>
              </tr>
            </thead>
            <tbody>
              {data.poster.map((p) => (
                <tr key={p._id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">
                    <Link to={`/sykkelhotell/${p._id}`} className="text-indigo-700 underline">{p.nr}</Link>
                  </td>
                  <td className="px-3 py-2">
                    {kundeNavn(p.kunde)}
                    <div className="text-slate-600">{p.kunde?.phoneNumber}</div>
                  </td>
                  <td className="px-3 py-2">
                    {sykkelTekst(p)}
                    {p.elsykkel && <div className="text-slate-600">Elsykkel{p.batteriMed ? ", batteri med" : ""}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${STATUS_CLS[p.status]}`}>
                      {STATUS_KORT[p.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {p.status === "innlevert" && `Hos ${STED_LABEL[p.innlevertSted]}`}
                    {p.status === "lagret" && (p.plass ? `Plass ${p.plass}` : "—")}
                    {["henting-bestilt", "klar"].includes(p.status) &&
                      `${fmtDato(p.henting?.dato)} hos ${STED_LABEL[p.henting?.sted] || ""}`}
                    {p.status === "utlevert" && fmtDato(p.avsluttetDato)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDato(p.betaltTil)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
